import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Zap, Check, X, RefreshCw, ExternalLink } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:8000';
const TABS = ['pending', 'approved', 'rejected'];

// Admin review queue for user-contributed Insider tips. Approve persists the
// stored extraction to the graph (no second LLM call); reject just closes it.
const AdminInsiderSubmissions = () => {
  const { token } = useAuth();
  const [tab, setTab] = useState('pending');
  const [items, setItems] = useState([]);
  const [pendingCount, setPendingCount] = useState(0);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [message, setMessage] = useState('');

  const headers = useCallback(
    () => ({ Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }),
    [token]
  );

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const resp = await fetch(
        `${API_BASE}/api/insider/submissions?status_filter=${tab}&limit=200`,
        { headers: headers() }
      );
      if (resp.ok) {
        const data = await resp.json();
        setItems(data.submissions || []);
        setPendingCount(data.pending_count || 0);
      } else {
        setMessage('Failed to load submissions.');
      }
    } catch {
      setMessage('Failed to load submissions.');
    }
    setLoading(false);
  }, [tab, headers]);

  useEffect(() => {
    if (token) load();
  }, [token, load]);

  const act = async (id, action) => {
    setBusyId(id);
    setMessage('');
    try {
      const resp = await fetch(`${API_BASE}/api/insider/submissions/${id}/${action}`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({}),
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.ok) {
        setItems((prev) => prev.filter((s) => s.id !== id));
        setPendingCount((c) => Math.max(0, c - 1));
        setMessage(
          action === 'approve'
            ? `Approved — ${data.new_events || 0} event(s) added to the graph.`
            : 'Rejected.'
        );
      } else {
        setMessage(data.detail || `Failed to ${action}.`);
      }
    } catch {
      setMessage(`Failed to ${action}.`);
    }
    setBusyId(null);
  };

  const fmt = (d) => (d ? new Date(d).toLocaleString() : '—');

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Link to="/admin" className="inline-flex items-center text-videbits-700 hover:text-videbits-900 mb-6">
          <ArrowLeft className="w-4 h-4 mr-1" /> Back to admin
        </Link>

        <div className="flex items-center justify-between mb-6">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Zap className="w-6 h-6 text-videbits-600" /> Insider submissions
            {pendingCount > 0 && (
              <span className="text-sm bg-videbits-600 text-white rounded-full px-2 py-0.5">
                {pendingCount} pending
              </span>
            )}
          </h1>
          <button onClick={load} className="inline-flex items-center gap-1 text-sm text-videbits-600 hover:text-videbits-800">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
        </div>

        <div className="flex gap-2 mb-4">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium capitalize transition-colors ${
                tab === t ? 'bg-videbits-600 text-white' : 'bg-videbits-50 text-videbits-700 hover:bg-videbits-100'
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {message && (
          <div className="mb-4 px-4 py-2 rounded-lg bg-videbits-50 text-videbits-800 text-sm">{message}</div>
        )}

        {loading ? (
          <p className="text-gray-500">Loading…</p>
        ) : items.length === 0 ? (
          <p className="text-gray-400">No {tab} submissions.</p>
        ) : (
          <div className="space-y-4">
            {items.map((s) => {
              const ex = s.extracted || {};
              return (
                <div key={s.id} className="bg-white rounded-xl shadow-sm p-5">
                  <div className="flex items-start justify-between gap-4">
                    <div className="min-w-0">
                      <p className="font-semibold text-gray-900">
                        {ex.project_title || s.headline || '(no title found)'}
                        {ex.media_type && (
                          <span className="ml-2 text-xs uppercase tracking-wide text-videbits-600">
                            {ex.media_type}
                          </span>
                        )}
                      </p>
                      {s.headline && ex.project_title && ex.project_title !== s.headline && (
                        <p className="text-sm text-gray-500 mt-0.5">{s.headline}</p>
                      )}
                      <ul className="mt-2 space-y-1">
                        {(ex.events || []).map((ev, i) => (
                          <li key={i} className="text-sm text-gray-700">
                            • {ev.event_type.replace(/_/g, ' ').toLowerCase()}
                            {ev.description ? ` — ${ev.description}` : ''}
                            {ev.metadata?.person && (
                              <span className="block ml-3 text-videbits-700">
                                {ev.metadata.person}
                                {ev.metadata.role ? ` · ${ev.metadata.role}` : ''}
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                      <div className="flex items-center gap-3 mt-3 text-xs text-gray-400">
                        <span>by {s.submitted_by || 'unknown'}</span>
                        <span>{fmt(s.created_at)}</span>
                        {s.raw_url && (
                          <a
                            href={s.raw_url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-0.5 text-videbits-600 hover:text-videbits-800"
                          >
                            source <ExternalLink className="w-3 h-3" />
                          </a>
                        )}
                      </div>
                    </div>
                    {tab === 'pending' && (
                      <div className="flex flex-col gap-2 flex-shrink-0">
                        <button
                          onClick={() => act(s.id, 'approve')}
                          disabled={busyId === s.id}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50"
                        >
                          <Check className="w-4 h-4" /> Approve
                        </button>
                        <button
                          onClick={() => act(s.id, 'reject')}
                          disabled={busyId === s.id}
                          className="inline-flex items-center gap-1 px-3 py-1.5 bg-videbits-50 border border-videbits-300 text-videbits-700 rounded-lg text-sm font-medium hover:bg-videbits-100 disabled:bg-gray-100 disabled:border-gray-300 disabled:text-gray-500"
                        >
                          <X className="w-4 h-4" /> Reject
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminInsiderSubmissions;
